import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Sheet,
  Stack,
  Table,
  Tooltip,
  Typography,
} from "@mui/joy";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import Loading from "../components/Loading";
import {
  useGrantMetalMindAdminUser,
  useListMetalMindAdminUsers,
  useRevokeMetalMindAdminUser,
} from "../services/metalmindAdmin";
import { useMetalMindAdminLayoutContext } from "./MetalMindAdminLayout";
import { MetalMindAdminUser } from "./metalmindAdmin/types";

interface MetalMindAdminUsersViewProps {
  currentUserId: string;
  items: MetalMindAdminUser[];
  loading?: boolean;
  error?: string | null;
  onGrant?: (userId: string) => Promise<void>;
  onRevoke?: (userId: string) => Promise<void>;
}

const USERS_PAGE_SIZE = 50;

const wrappingCellSx = {
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
} as const;

const renderRevokeReason = (
  currentUserId: string,
  user: MetalMindAdminUser,
) => {
  if (user.id === currentUserId) {
    return "You cannot revoke your own MetalMind Admin access.";
  }
  if (user.bootstrap_via_env_var) {
    return "Bootstrap users are controlled by environment configuration.";
  }
  return null;
};

export const MetalMindAdminUsersView: React.FC<
  MetalMindAdminUsersViewProps
> = ({
  currentUserId,
  items,
  loading = false,
  error = null,
  onGrant,
  onRevoke,
}) => {
  const [pendingGrantId, setPendingGrantId] = useState<string | null>(null);
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const sortedItems = useMemo(
    () =>
      [...items].sort((left, right) => {
        return (
          Number(right.is_metalmind_admin) - Number(left.is_metalmind_admin) ||
          left.email.localeCompare(right.email)
        );
      }),
    [items],
  );

  const totalPages = Math.max(
    1,
    Math.ceil(sortedItems.length / USERS_PAGE_SIZE),
  );
  const pagedItems = useMemo(
    () =>
      sortedItems.slice(
        page * USERS_PAGE_SIZE,
        page * USERS_PAGE_SIZE + USERS_PAGE_SIZE,
      ),
    [page, sortedItems],
  );

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, totalPages - 1));
  }, [totalPages]);

  const handleGrant = async (userId: string) => {
    if (!onGrant) return;
    try {
      setPendingGrantId(userId);
      await onGrant(userId);
      setActionError(null);
    } catch (err: any) {
      setActionError(err?.message || "Failed to grant MetalMind Admin access.");
    } finally {
      setPendingGrantId(null);
    }
  };

  const handleRevoke = async (userId: string) => {
    if (!onRevoke) return;
    try {
      setPendingRevokeId(userId);
      await onRevoke(userId);
      setActionError(null);
    } catch (err: any) {
      setActionError(
        err?.message || "Failed to revoke MetalMind Admin access.",
      );
    } finally {
      setPendingRevokeId(null);
    }
  };

  return (
    <Stack spacing={2}>
      {error && (
        <Alert color="danger" variant="soft">
          {error}
        </Alert>
      )}
      {actionError && (
        <Alert color="danger" variant="soft">
          {actionError}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AdminPanelSettingsIcon color="primary" />
              <Typography level="h3">MetalMind Admin users</Typography>
            </Stack>

            {loading ? (
              <Loading />
            ) : sortedItems.length === 0 ? (
              <Alert color="neutral" variant="soft">
                No users are visible in the current organization scope.
              </Alert>
            ) : (
              <Sheet
                variant="outlined"
                sx={{ borderRadius: "8px", overflowX: "auto" }}
              >
                <Table
                  hoverRow
                  sx={{
                    minWidth: 1420,
                    tableLayout: "fixed",
                    "& th": {
                      whiteSpace: "nowrap",
                    },
                    "& td": {
                      verticalAlign: "top",
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: 280 }}>Email</th>
                      <th style={{ width: 180 }}>Name</th>
                      <th style={{ width: 180 }}>Organization</th>
                      <th style={{ width: 250 }}>Status</th>
                      <th style={{ width: 240 }}>Granted By</th>
                      <th style={{ width: 190 }}>Granted At</th>
                      <th style={{ width: 120, textAlign: "right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedItems.map((user) => {
                      const revokeReason = renderRevokeReason(
                        currentUserId,
                        user,
                      );

                      return (
                        <tr key={user.id}>
                          <td>
                            <Typography level="body-sm" sx={wrappingCellSx}>
                              {user.email}
                            </Typography>
                          </td>
                          <td>
                            <Typography level="body-sm" sx={wrappingCellSx}>
                              {user.name || "—"}
                            </Typography>
                          </td>
                          <td>
                            <Typography level="body-sm" sx={wrappingCellSx}>
                              {user.organization_name || "—"}
                            </Typography>
                          </td>
                          <td>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              <Chip
                                size="sm"
                                variant="soft"
                                color={
                                  user.is_metalmind_admin
                                    ? "primary"
                                    : "neutral"
                                }
                              >
                                {user.is_metalmind_admin
                                  ? "MetalMind Admin"
                                  : "User"}
                              </Chip>
                              {user.bootstrap_via_env_var && (
                                <Chip size="sm" variant="soft" color="warning">
                                  bootstrap_via_env_var
                                </Chip>
                              )}
                            </Stack>
                          </td>
                          <td>
                            <Typography level="body-sm" sx={wrappingCellSx}>
                              {user.granted_by_email || "—"}
                            </Typography>
                          </td>
                          <td>
                            <Typography
                              level="body-sm"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {user.granted_at || "—"}
                            </Typography>
                          </td>
                          <td>
                            <Stack direction="row" justifyContent="flex-end">
                              {user.is_metalmind_admin ? (
                                revokeReason ? (
                                  <Tooltip title={revokeReason}>
                                    <Box>
                                      <Button
                                        size="sm"
                                        variant="plain"
                                        color="danger"
                                        disabled
                                      >
                                        Revoke
                                      </Button>
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="plain"
                                    color="danger"
                                    loading={pendingRevokeId === user.id}
                                    onClick={() => handleRevoke(user.id)}
                                  >
                                    Revoke
                                  </Button>
                                )
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outlined"
                                  color="primary"
                                  loading={pendingGrantId === user.id}
                                  onClick={() => handleGrant(user.id)}
                                >
                                  Grant
                                </Button>
                              )}
                            </Stack>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography level="body-sm" color="neutral">
                    Showing {page * USERS_PAGE_SIZE + 1}-
                    {Math.min((page + 1) * USERS_PAGE_SIZE, sortedItems.length)}{" "}
                    of {sortedItems.length}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      disabled={page === 0}
                      onClick={() => setPage((currentPage) => currentPage - 1)}
                    >
                      Previous
                    </Button>
                    <Typography
                      level="body-sm"
                      color="neutral"
                      sx={{
                        alignSelf: "center",
                        minWidth: 72,
                        textAlign: "center",
                      }}
                    >
                      Page {page + 1} / {totalPages}
                    </Typography>
                    <Button
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((currentPage) => currentPage + 1)}
                    >
                      Next
                    </Button>
                  </Stack>
                </Stack>
              </Sheet>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

const MetalMindAdminUsers: React.FC = () => {
  const { user } = useMetalMindAdminLayoutContext();
  const listUsers = useListMetalMindAdminUsers();
  const grantUser = useGrantMetalMindAdminUser();
  const revokeUser = useRevokeMetalMindAdminUser();
  const [items, setItems] = useState<MetalMindAdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const rows = await listUsers();
      setItems(rows);
    } catch (err: any) {
      setError(err?.message || "Failed to load MetalMind Admin users.");
    } finally {
      setLoading(false);
    }
  }, [listUsers]);

  useEffect(() => {
    load();
  }, [load]);

  const handleGrant = useCallback(
    async (userId: string) => {
      await grantUser(userId);
      await load();
    },
    [grantUser, load],
  );

  const handleRevoke = useCallback(
    async (userId: string) => {
      await revokeUser(userId);
      await load();
    },
    [load, revokeUser],
  );

  return (
    <MetalMindAdminUsersView
      currentUserId={user.id}
      items={items}
      loading={loading}
      error={error}
      onGrant={handleGrant}
      onRevoke={handleRevoke}
    />
  );
};

export default MetalMindAdminUsers;
